import { useEffect, useRef } from 'react'
import { useSubscription } from '@refinedev/core'

const DEFAULT_DEBOUNCE_MS = 2000

interface UseLiveRefetchOptions {
	channel: string
	enabled?: boolean
	refetch: () => unknown
	/** Fires for every event, including ones coalesced into a single refetch. */
	onLiveEvent?: (event: { type: string }) => void
	/** Events arriving within this window are coalesced into one refetch. */
	debounceMs?: number
}

export function useLiveRefetch({ channel, enabled = true, refetch, onLiveEvent, debounceMs = DEFAULT_DEBOUNCE_MS }: UseLiveRefetchOptions) {
	const refetchRef = useRef(refetch)
	refetchRef.current = refetch
	const onLiveEventRef = useRef(onLiveEvent)
	onLiveEventRef.current = onLiveEvent
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		const clear = () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
		if (!enabled) clear()
		return clear
	}, [enabled])

	useSubscription({
		channel,
		types: ['*'],
		enabled,
		onLiveEvent: (event) => {
			onLiveEventRef.current?.(event)
			if (timeoutRef.current) return
			timeoutRef.current = setTimeout(() => {
				timeoutRef.current = null
				void refetchRef.current()
			}, debounceMs)
		},
	})
}
